// common
import {
  VuexModule,
  Module,
  getModule,
  Mutation,
} from "vuex-module-decorators";
import store from "@/store";

// helpers
import { IFiskalyState } from "@/interfaces/store/IFiskalyState";
import BookingModule from "./booking";
import { ITransaction } from "@/types/db/transaction";

@Module({ dynamic: true, store, name: "Fiskaly", namespaced: true })
class FiskalyStoreModule extends VuexModule implements IFiskalyState {
  public signedReceiptsQrCodes: {
    bookingUuid: string;
    receiptQrCode: string;
    receiptUuid: string;
    receiptNumber: string;
  }[] = [];

  public transactionsInProgress: {
    [bookingUuid: string]: ITransaction;
  } = {};

  // array for the unsynced transactions per cashfloat
  public unsyncedTransactions: {
    [cashfloatUuid: string]: string[];
  } = {};

  // below is used to keep track of the sequential id
  public lastTransaction: ITransaction | null = null;

  public get getSelectedBookingReceiptSignature() {
    const selectedBookingUuid = BookingModule.selectedBookingUUID;
    const signature = this.signedReceiptsQrCodes.find(
      (s) => s.bookingUuid === selectedBookingUuid
    );
    return signature;
  }

  @Mutation
  setSignatureQrCodes(
    signedReceiptsQrCode: {
      bookingUuid: string;
      receiptQrCode: string;
      receiptUuid: string;
      receiptNumber: string;
    }[]
  ) {
    this.signedReceiptsQrCodes = signedReceiptsQrCode;
  }

  @Mutation
  removeSignatureQrCodes(bookingUuid: string) {
    this.signedReceiptsQrCodes = this.signedReceiptsQrCodes.filter(
      (s) => s.bookingUuid !== bookingUuid
    );
  }

  @Mutation
  clearAllSignature() {
    this.signedReceiptsQrCodes = [];
  }

  @Mutation
  addSignatureQrCode(signedReceiptsQrCode: {
    bookingUuid: string;
    receiptQrCode: string;
    receiptUuid: string;
    receiptNumber: string;
  }) {
    this.signedReceiptsQrCodes.push(signedReceiptsQrCode);
  }

  @Mutation
  addNewTransaction(transaction: ITransaction) {
    this.transactionsInProgress[transaction.bookingUuid] = transaction;
    this.lastTransaction = transaction;
  }

  @Mutation
  removeTransaction(bookingUuid: string) {
    if (this.transactionsInProgress[bookingUuid]) {
      delete this.transactionsInProgress[bookingUuid];
    }
  }

  @Mutation
  clearTransactions() {
    this.transactionsInProgress = {};
  }

  @Mutation
  addUnsyncedTransaction(payload: { cashfloatUuid: string; uuid: string }) {
    if (!this.unsyncedTransactions[payload.cashfloatUuid]) {
      this.unsyncedTransactions[payload.cashfloatUuid] = [];
    }
    this.unsyncedTransactions[payload.cashfloatUuid].push(payload.uuid);
  }

  @Mutation
  removeUnsyncedTransaction(payload: { cashfloatUuid: string; uuid: string }) {
    const { cashfloatUuid, uuid } = payload;
    if (!this.unsyncedTransactions[cashfloatUuid]) return;
    this.unsyncedTransactions[cashfloatUuid] = this.unsyncedTransactions[
      cashfloatUuid
    ].filter((t) => t !== uuid);
    if (!this.unsyncedTransactions[cashfloatUuid].length) {
      delete this.unsyncedTransactions[cashfloatUuid];
    }
  }

  /**
   * Resotres fiskaly module fields saved in localstorage
   */
  @Mutation
  restore(commonFields: Partial<IFiskalyState>) {
    const {
      signedReceiptsQrCodes,
      transactionsInProgress,
      lastTransaction,
      unsyncedTransactions,
    } = commonFields;
    // console.log("commonFields => ", commonFields);

    if (signedReceiptsQrCodes) {
      this.signedReceiptsQrCodes = signedReceiptsQrCodes;
    }
    if (transactionsInProgress) {
      this.transactionsInProgress = transactionsInProgress;
    }
    if (lastTransaction) {
      this.lastTransaction = lastTransaction;
    }
    if (unsyncedTransactions) {
      this.unsyncedTransactions = unsyncedTransactions;
    }
  }
}

const FiskalyModule = getModule(FiskalyStoreModule);
export default FiskalyModule;
